import { Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ESCENARIO_PACKS } from '@/lib/escenarios';
import { formatPrecio } from '@/seo/landings';

// Sección de la home que presenta los packs de escenario (dioramas) y enlaza a
// /escenarios-comunion (enlace interno rastreable) y al configurador, donde el
// escenario se elige en su propio paso. Nombres, título y descripción reutilizan
// las claves del paso «Escenario» del configurador, ya traducidas.
export function EscenariosTeaser({ onDesign }: { onDesign: () => void }) {
  const { t } = useTranslation();
  return (
    <section id="escenarios" className="py-20" aria-labelledby="escenarios-title">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#866A12] mb-3">
              {t('home.scenesKicker', { defaultValue: 'Nuevo · Escenarios' })}
            </p>
            <h2 id="escenarios-title" className="text-3xl sm:text-4xl font-semibold mb-4" style={{ color: 'var(--page-heading)' }}>
              {t('configurator.sceneTitle', { defaultValue: '¿Le añadimos un escenario?' })}
            </h2>
            <p className="text-[#374151] leading-relaxed">
              {t('configurator.sceneHint', { defaultValue: 'Un pequeño diorama hecho a mano para que la figura luzca como en el día de la comunión. Es opcional.' })}
            </p>
          </div>

          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 list-none p-0 m-0 mb-4">
            {ESCENARIO_PACKS.map(pack => {
              const nombre = t(`configurator.scene_${pack.id}`, { defaultValue: pack.id });
              const desc = t(`configurator.scene_${pack.id}Desc`, { defaultValue: '' });
              return (
                <li key={pack.id} className={`rounded-2xl bg-white shadow-elegant overflow-hidden flex sm:flex-col ${pack.desde ? 'ring-2 ring-[#D4AF37]/60' : ''}`}>
                  <img
                    src={pack.image}
                    alt={desc ? `${nombre}: ${desc}` : nombre}
                    width={900}
                    height={900}
                    loading="lazy"
                    className="w-28 h-28 sm:w-full sm:h-auto flex-shrink-0 object-cover bg-white"
                  />
                  <div className="p-4 min-w-0 flex flex-col justify-center">
                    {pack.desde && (
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-[#866A12]">{t('configurator.sceneReplicaBadge', { defaultValue: 'Pieza única' })}</p>
                    )}
                    <p className="font-semibold text-lg" style={{ color: 'var(--page-heading)' }}>{nombre}</p>
                    <p className="text-sm text-[#6B7280] leading-snug mb-1">{desc}</p>
                    <p className="text-xl font-bold text-gradient-gold whitespace-nowrap">
                      {pack.desde ? t('configurator.sceneFrom', { price: pack.price.toFixed(0), defaultValue: `desde ${pack.price.toFixed(0)} €` }) : formatPrecio(pack.price)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>

          <p className="text-[11px] text-center text-[#6B7280] mb-8">
            {t('configurator.sceneFootnote', { defaultValue: 'Imágenes ilustrativas: cada escenario se hace a mano para tu figura. ¿Quieres otro distinto? Escríbenos y te lo presupuestamos.' })}
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            <button type="button" onClick={onDesign} className="btn-nacre inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium">
              <Sparkles className="w-4 h-4" aria-hidden="true" /> {t('home.selectPlan', { defaultValue: 'Crear mi figura' })}
            </button>
            <a href="/escenarios-comunion" className="inline-flex items-center px-6 py-3 rounded-full font-medium border border-[#D4AF37]/60 hover:bg-[#D4AF37]/10" style={{ color: 'var(--page-heading)' }}>
              {t('home.scenesMore', { defaultValue: 'Ver escenarios' })}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
